import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import { Zap, Plus, Pencil, Trash2, Search, MapPin, Eye } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const ElectricityPage = () => {
  const { token, isAdmin } = useAuth();
  const [meters, setMeters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [selectedMeter, setSelectedMeter] = useState(null);
  const [formData, setFormData] = useState({
    account_number: '',
    meter_number: '',
    location: '',
    property_name: '',
    notes: ''
  });

  const fetchMeters = async () => {
    try {
      const response = await axios.get(`${API}/electricity`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMeters(response.data);
    } catch (error) {
      toast.error('خطأ في تحميل البيانات');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMeters();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedMeter) {
        await axios.put(`${API}/electricity/${selectedMeter.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('تم تحديث العداد بنجاح');
      } else {
        await axios.post(`${API}/electricity`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('تم إضافة العداد بنجاح');
      }
      setIsDialogOpen(false);
      fetchMeters();
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'حدث خطأ');
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/electricity/${selectedMeter.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('تم حذف العداد بنجاح');
      setIsDeleteDialogOpen(false);
      fetchMeters();
    } catch (error) {
      toast.error('خطأ في حذف العداد');
    }
  };

  const resetForm = () => {
    setFormData({
      account_number: '',
      meter_number: '',
      location: '',
      property_name: '',
      notes: ''
    });
    setSelectedMeter(null);
  };

  const openEditDialog = (meter) => {
    setSelectedMeter(meter);
    setFormData({
      account_number: meter.account_number,
      meter_number: meter.meter_number,
      location: meter.location || '',
      property_name: meter.property_name || '',
      notes: meter.notes || ''
    });
    setIsDialogOpen(true);
  };

  const openViewDialog = (meter) => {
    setSelectedMeter(meter);
    setIsViewDialogOpen(true);
  };

  const filteredMeters = meters.filter(meter =>
    (meter.account_number || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (meter.meter_number || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (meter.property_name || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="spinner w-8 h-8"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="electricity-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-slate-900">عدادات الكهرباء</h1>
          <p className="text-slate-500 mt-1">إدارة عدادات الكهرباء والحسابات</p>
        </div>
        {isAdmin && (
          <Button
            onClick={() => { resetForm(); setIsDialogOpen(true); }}
            className="text-white" style={{ background: '#1a365d' }}
            data-testid="add-electricity-btn"
          >
            <Plus className="w-4 h-4 ml-2" />
            إضافة عداد
          </Button>
        )}
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          placeholder="البحث عن عداد..."
          className="pr-10"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          data-testid="search-electricity"
        />
      </div>

      {/* Meters Table */}
      <Card className="border-0 shadow-sm" data-testid="electricity-table-card">
        <CardContent className="p-0">
          <ScrollArea className="w-full">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="text-center w-24">عرض</TableHead>
                  {isAdmin && <TableHead className="text-center w-24">إجراءات</TableHead>}
                  <TableHead className="text-center">الموقع</TableHead>
                  <TableHead className="text-center">رقم العداد</TableHead>
                  <TableHead className="text-center">رقم الحساب</TableHead>
                  <TableHead className="text-right">العقار</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredMeters.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={isAdmin ? 6 : 5} className="text-center py-8 text-slate-400">
                      <Zap className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>لا توجد عدادات</p>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredMeters.map((meter) => (
                    <TableRow key={meter.id} className="table-row-hover" data-testid={`electricity-row-${meter.id}`}>
                      <TableCell className="text-center">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-blue-600 hover:text-blue-700"
                          onClick={() => openViewDialog(meter)}
                          data-testid={`view-electricity-${meter.id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </TableCell>
                      {isAdmin && (
                        <TableCell className="text-center">
                          <div className="flex items-center gap-2 justify-center">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openEditDialog(meter)}
                              data-testid={`edit-electricity-${meter.id}`}
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-red-500 hover:text-red-600"
                              onClick={() => { setSelectedMeter(meter); setIsDeleteDialogOpen(true); }}
                              data-testid={`delete-electricity-${meter.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      )}
                      <TableCell className="text-center">
                        {meter.location && (
                          <span className="text-sm text-slate-500 flex items-center gap-1 justify-center">
                            <MapPin className="w-3 h-3" />
                            {meter.location}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        <span className="number">{meter.meter_number}</span>
                      </TableCell>
                      <TableCell className="text-center">
                        <span className="font-medium number">{meter.account_number}</span>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center gap-3 justify-end">
                          <div className="text-right">
                            <p className="font-medium text-slate-900">{meter.property_name || 'غير محدد'}</p>
                          </div>
                          <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
                            <Zap className="w-5 h-5 text-amber-600" />
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </ScrollArea>
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-md" dir="rtl">
          <DialogHeader>
            <DialogTitle>{selectedMeter ? 'تعديل العداد' : 'إضافة عداد جديد'}</DialogTitle>
            <DialogDescription>أدخل بيانات عداد الكهرباء</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>اسم العقار</Label>
              <Input
                value={formData.property_name}
                onChange={(e) => setFormData({ ...formData, property_name: e.target.value })}
                placeholder="فيلا الرياض، شقة جدة..."
                data-testid="electricity-property-input"
              />
            </div>
            <div className="space-y-2">
              <Label>رقم الحساب</Label>
              <Input
                value={formData.account_number}
                onChange={(e) => setFormData({ ...formData, account_number: e.target.value })}
                required
                data-testid="electricity-account-input"
              />
            </div>
            <div className="space-y-2">
              <Label>رقم العداد</Label>
              <Input
                value={formData.meter_number}
                onChange={(e) => setFormData({ ...formData, meter_number: e.target.value })}
                required
                data-testid="electricity-meter-input"
              />
            </div>
            <div className="space-y-2">
              <Label>الموقع</Label>
              <Input
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                placeholder="المدينة، الحي..."
                data-testid="electricity-location-input"
              />
            </div>
            <div className="space-y-2">
              <Label>ملاحظات</Label>
              <Input
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="ملاحظات إضافية..."
                data-testid="electricity-notes-input"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                إلغاء
              </Button>
              <Button type="submit" className="text-white" style={{ background: '#1a365d' }} data-testid="save-electricity-btn">
                {selectedMeter ? 'حفظ التعديلات' : 'إضافة العداد'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent dir="rtl">
          <AlertDialogHeader className="text-right">
            <AlertDialogTitle className="text-right">تأكيد الحذف</AlertDialogTitle>
            <AlertDialogDescription className="text-right">
              هل أنت متأكد من حذف هذا العداد؟ لا يمكن التراجع عن هذا الإجراء.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-row-reverse gap-2">
            <AlertDialogCancel>إلغاء</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-500 hover:bg-red-600"
              data-testid="confirm-delete-electricity"
            >
              حذف
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* View Dialog */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="max-w-md" dir="rtl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Zap className="w-5 h-5 text-amber-500" />
              تفاصيل عداد الكهرباء
            </DialogTitle>
          </DialogHeader>
          {selectedMeter && (
            <div className="space-y-4">
              {/* Meter Header */}
              <div className="flex items-center gap-4 p-4 bg-amber-50 rounded-xl">
                <div className="w-14 h-14 bg-amber-100 rounded-xl flex items-center justify-center">
                  <Zap className="w-7 h-7 text-amber-600" />
                </div>
                <div>
                  <h3 className="text-lg font-bold" style={{ color: '#1a365d' }}>
                    {selectedMeter.property_name || 'غير محدد'}
                  </h3>
                  {selectedMeter.location && (
                    <p className="text-slate-500 text-sm flex items-center gap-1">
                      <MapPin className="w-3 h-3" />
                      {selectedMeter.location}
                    </p>
                  )}
                </div>
              </div>

              {/* Details */}
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-xs text-slate-500 mb-1">رقم الحساب</p>
                  <p className="font-bold number" style={{ color: '#1a365d' }}>{selectedMeter.account_number}</p>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-xs text-slate-500 mb-1">رقم العداد</p>
                  <p className="font-bold number" style={{ color: '#1a365d' }}>{selectedMeter.meter_number}</p>
                </div>
              </div>

              {/* Notes */}
              {selectedMeter.notes && (
                <div className="p-4 bg-amber-50 rounded-xl border border-amber-100">
                  <p className="text-xs text-slate-500 mb-1">ملاحظات</p>
                  <p className="text-slate-700">{selectedMeter.notes}</p>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsViewDialogOpen(false)}>
              إغلاق
            </Button>
            {isAdmin && (
              <Button 
                className="text-white" 
                style={{ background: '#1a365d' }}
                onClick={() => { setIsViewDialogOpen(false); openEditDialog(selectedMeter); }}
              >
                تعديل
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ElectricityPage;
